import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { RecordingsService } from 'src/app/recordings/recordings.service';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

interface SignalRow {
  user: string;
  userShort: string;
  leadScore: number;
  pagesLength: number | string;
  duration: string;
}

/**
 * Compact Signals table for the Shopify LIVE dashboard.
 *
 * Mirrors the first-5 slice of the Signals Insights > Overview table
 * (see signals-insights/overview). Reuses the same data source
 * (RecordingsService.getScreenRecorders with signals: true) but renders only
 * the Score / User / Pages / Duration columns as in the mockup.
 */
@Component({
  selector: 'app-shopify-signals-table',
  templateUrl: './shopify-signals-table.component.html',
  styleUrls: ['./shopify-signals-table.component.css'],
})
export class ShopifySignalsTableComponent implements OnInit {
  private recordingsService = inject(RecordingsService);
  private targetService = inject(CoreTargetService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);

  public rows: SignalRow[] = [];
  public isLoading = true;

  ngOnInit(): void {
    this.loadSignals();
  }

  private loadSignals(): void {
    this.isLoading = true;

    const params = {
      startDate: this.localStorageService.get('startDate'),
      endDate: this.localStorageService.get('endDate'),
      target: this.targetService.getSelectedTarget('target'),
      page: 1,
      pageSize: 5,
      signals: true,
    };

    this.recordingsService.getScreenRecorders(params).subscribe({
      next: (data: any) => {
        const sessions = data?.sessionReplays || [];
        this.rows = sessions.slice(0, 5).map((s: any) => this.toRow(s));
        this.isLoading = false;
      },
      error: (err: any) => {
        console.log(err);
        this.rows = [];
        this.isLoading = false;
      },
    });
  }

  private toRow(s: any): SignalRow {
    return {
      user: s.tempSessionID,
      userShort: this.subString(s.tempSessionID, 0, 10),
      leadScore: s.lead_score ?? 0,
      pagesLength: s.pageVisitedLength,
      duration: this.formatTimeZero(s.duration),
    };
  }

  public leadScoreClass(score: number): string {
    if (score < 30) return 'low_value';
    if (score <= 70) return 'mid_value';
    return 'high_value';
  }

  // Opens the full Signals overview.
  public view(): void {
    this.router.navigate(['/signals']);
  }

  // ── helpers copied from signals-insights/overview so formatting matches ──
  private subString(str: string, start: number, end: number): string {
    if (!str) return '';
    return str.length > end ? str.substring(start, end) + '...' : str;
  }

  private formatTimeZero(input: string): string {
    try {
      if (!input) return '—';
      let str = '';

      if (input.split(':')[0] != '00') {
        str +=
          input.split(':')[0].split('')[0] == '0'
            ? input.split(':')[0].split('')[1] + ':'
            : input.split(':')[0] + ':';
      }

      str +=
        input.split(':')[1].split('')[0] == '0'
          ? input.split(':')[1].split('')[1] + ':'
          : input.split(':')[1] + ':';

      str += input.split(':')[2];
      str += input.split(':')[0] != '00' ? ' h' : ' m';

      return str;
    } catch (e) {
      return '—';
    }
  }
}
